<template>
  <!-- CLOSED -->
  <Container
    id="jump-navigation"
    tag="nav"
    :uses-container-query="false"
    indent="level-1"
    class="
      fixed bottom-0 left-0 z-30 flex w-full items-center justify-between bg-peach-550 py-5 text-violet-text
      2nd:bg-violet-dark-600
      lg:hidden
    "
    role="navigation"
  >
    <DynLink
      :to="{ name: 'StudentIndex' }"
      aria-labelledby="link zur Startseite"
      class="group ring-offset-8 focus:outline-none focus-visible:ring-2 focus-visible:ring-focusTeacher"
    >
      <IconLogo
        class="h-6.5 [&_path]:transition-colors group-hover:[&_path]:!fill-peach-500"
        :type="isLowerGrade ? 'invert' : 'student'"
      />
      <IconTrialLogo v-if="auth.student?.organisation?.is_trial" class="text-white" />
    </DynLink>

    <button
      class="group -mr-4 flex h-9 gap-4 px-4 py-1 text-white transition-opacity typo-heading-s-semibold"
      type="button"
      aria-labelledby="open mobile navigation"
      @click="isOpen = true"
    >
      <span> Menü </span>
      <IconMenuBurger class="h-full text-white" />
    </button>
  </Container>

  <!-- OPEN -->
  <Container
    v-if="auth.user"
    tag="nav"
    indent="level-1"
    :uses-container-query="false"
    class="
      fixed inset-0 z-30 flex w-full flex-col items-center justify-end overflow-hidden bg-peach-550 py-5
      text-violet-text transition-all ease-[cubic-bezier(0.12,0.85,0,0.98)]
      2nd:bg-violet-dark-600
      lg:hidden
    "
    role="navigation"
    :aria-expanded="isOpen"
    :aria-labelledby="isOpen ? 'Mobile Navigation is open' : 'hidden'"
    :class="
      isOpen
        ? 'disable-body-scroll top-0 duration-500 ease-[cubic-bezier(0.12,0.85,0,0.98)]'
        : 'top-full duration-75 ease-in'
    "
  >
    <div class="w-full">
      <div class="mb-[15svh] flex h-fit w-full flex-col items-center space-y-12">
        <menu class="flex w-full flex-col items-center justify-end gap-2 text-white">
          <ul class="flex flex-col items-start gap-12 typo-heading-xl-black md:typo-heading-2xl-black">
            <li class="mb-[8vh] flex w-full justify-center gap-2.5 md:gap-3.5">
              <UiStudentIcon
                :initials="auth.student!.initials"
                :full-name="auth.student!.full_name"
                size="sm"
                class="shrink-0"
              />
              <button
                type="button"
                class="
                  underline-link ring-offset-4 ring-offset-violet-dark-600 typo-heading-m-semibold
                  focus:outline-none
                  focus-visible:ring-2 focus-visible:ring-focusTeacher
                  md:typo-heading-l-bold
                "
                @click="auth.logout()"
              >
                <span class="block translate-y-1"> abmelden </span>
              </button>
            </li>
            <li>
              <DynLink
                :to="{ name: 'StudentIndex' }"
                class="
                  underline-link flex gap-3 whitespace-nowrap ring-offset-4
                  focus:outline-none
                  focus-visible:ring-2 focus-visible:ring-focusTeacher
                  md:gap-4
                "
                exact-active-class="active"
              >
                <IconHome class="size-8 shrink-0" /> Deine Übersicht
              </DynLink>
            </li>
            <li>
              <DynLink
                :to="{ name: 'StudentPaths' }"
                class="
                  underline-link flex gap-3 whitespace-nowrap ring-offset-4
                  focus:outline-none
                  focus-visible:ring-2 focus-visible:ring-focusTeacher
                  md:gap-4
                "
                active-class="active"
              >
                <IconRoute class="size-8 shrink-0" /> LernWege
              </DynLink>
            </li>
            <li>
              <DynLink
                :to="{ name: 'StudentFeedback' }"
                class="
                  underline-link flex gap-3 whitespace-nowrap ring-offset-4
                  focus:outline-none
                  focus-visible:ring-2 focus-visible:ring-focusTeacher
                  md:gap-4
                "
                active-class="active"
              >
                <IconChat class="size-8 shrink-0" /> Rückmeldungen
              </DynLink>
            </li>
          </ul>
        </menu>
      </div>
      <div class="flex w-full items-center justify-between">
        <DynLink
          :to="{ name: 'StudentIndex' }"
          aria-labelledby="link zurStartseite"
          class="group ring-offset-8 focus:outline-none focus-visible:ring-2 focus-visible:ring-focusTeacher"
        >
          <IconLogo
            class="h-6.5 [&_path]:transition-colors group-hover:[&_path]:!fill-peach-500"
            :type="isLowerGrade ? 'invert' : 'student'"
          />
          <IconTrialLogo v-if="auth.student?.organisation?.is_trial" class="text-white" />
        </DynLink>

        <button
          class="
            group -mr-4 flex h-9 items-center px-4 py-1 text-white transition-opacity
            hover:text-peach-100
            2nd:hover:text-violet-100
          "
          type="button"
          @click="isOpen = false"
        >
          <span class="mr-4 font-ingra text-base font-semibold transition-all group-hover:mr-3">
            {{ $t('general.close') }}
          </span>
          <IconCross class="h-full" />
        </button>
      </div>
    </div>
  </Container>
</template>

<script lang="ts" setup>
const isOpen = ref(false)
const route = useRoute()

const auth = useAuthStore()
const { isLowerGrade } = useStudentSchoolYears(auth.student!)

watch(
  () => route.path,
  () => {
    isOpen.value = false
  },
)
</script>

<style>
body:has(.disable-body-scroll) {
  overflow: hidden;
}
</style>
